'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Facility {
  id: number;
  name: string;
  location: string;
  size: string;
  capabilities: string[];
  certifications: string[];
  image: string;
  alt: string;
}

interface FacilityCapabilitiesProps {
  className?: string;
}

const FacilityCapabilities = ({ className = '' }: FacilityCapabilitiesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'facilities' | 'equipment' | 'quality'>('facilities');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const facilities: Facility[] = [
  {
    id: 1,
    name: 'Advanced Manufacturing Center',
    location: 'Seattle, WA',
    size: '150,000 sq ft',
    capabilities: ['5-Axis CNC Machining', 'Additive Manufacturing', 'Sheet Metal Fabrication', 'Assembly & Integration'],
    certifications: ['AS9100D', 'ITAR Registered', 'ISO 9001:2015'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_176b49e1c-1764654653366.png",
    alt: 'Modern aerospace manufacturing facility with CNC machines and clean industrial environment'
  },
  {
    id: 2,
    name: 'Precision Components Facility',
    location: 'Huntsville, AL',
    size: '85,000 sq ft',
    capabilities: ['Precision Machining', 'Quality Testing', 'Clean Room Assembly', 'Composite Manufacturing'],
    certifications: ['AS9100D', 'NADCAP Accredited', 'ITAR Registered'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14522924c-1767251693062.png",
    alt: 'Precision manufacturing workspace with advanced machining equipment and quality control stations'
  },
  {
    id: 3,
    name: 'Defense Systems Production',
    location: 'Colorado Springs, CO',
    size: '120,000 sq ft',
    capabilities: ['Defense Manufacturing', 'Secure Assembly', 'Testing & Validation', 'Program Management'],
    certifications: ['AS9100D', 'ITAR Registered', 'Secret Facility Clearance'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a681f6ac-1766493773415.png",
    alt: 'Secure defense manufacturing facility with controlled access and specialized equipment'
  }];


  const equipment = [
  { name: '5-Axis CNC Machines', count: '25+', icon: 'CogIcon' },
  { name: 'Metal 3D Printers', count: '12', icon: 'CubeIcon' },
  { name: 'CMM Inspection Systems', count: '8', icon: 'MagnifyingGlassIcon' },
  { name: 'Clean Room Stations', count: '6', icon: 'ShieldCheckIcon' },
  { name: 'Testing Equipment', count: '15+', icon: 'BeakerIcon' },
  { name: 'Assembly Workstations', count: '40+', icon: 'WrenchScrewdriverIcon' }];


  const qualityCapabilities = [
  {
    title: 'Advanced Metrology',
    description: 'State-of-the-art CMM systems with sub-micron accuracy for precision measurement and quality verification.',
    icon: 'ChartBarIcon'
  },
  {
    title: 'Non-Destructive Testing',
    description: 'Comprehensive NDT capabilities including X-ray, ultrasonic, and dye penetrant inspection methods.',
    icon: 'EyeIcon'
  },
  {
    title: 'Material Testing',
    description: 'Full material characterization and testing laboratory for aerospace-grade materials verification.',
    icon: 'BeakerIcon'
  },
  {
    title: 'Environmental Testing',
    description: 'Thermal cycling, vibration testing, and environmental simulation for aerospace qualification.',
    icon: 'CloudIcon'
  }];


  if (!isHydrated) {
    return (
      <section className={`py-20 lg:py-28 bg-background ${className}`}>
        <div className="mx-auto px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
                <span className="font-cta text-xs text-accent tracking-wider">FACILITY CAPABILITIES</span>
              </div>
              <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
                World-Class Manufacturing Infrastructure
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {facilities.map((facility) =>
              <div key={facility.id} className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <AppImage
                    src={facility.image}
                    alt={facility.alt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="p-6">
                    <h3 className="font-headline text-xl font-bold text-foreground mb-2">
                      {facility.name}
                    </h3>
                    <p className="font-body text-sm text-technical-gray mb-4">
                      {facility.location} • {facility.size}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className={`py-20 lg:py-28 bg-background ${className}`}>
      <div className="mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
              <span className="font-cta text-xs text-accent tracking-wider">FACILITY CAPABILITIES</span>
            </div>
            <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
              World-Class Manufacturing Infrastructure
            </h2>
            <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
              Our state-of-the-art facilities combine advanced manufacturing equipment with rigorous quality systems to deliver aerospace-grade precision at scale.
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-card border border-border rounded-lg p-1">
              <button
                onClick={() => setActiveTab('facilities')}
                className={`px-6 py-3 font-body text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'facilities' ? 'bg-accent text-primary' : 'text-technical-gray hover:text-foreground'}`
                }>

                Facilities
              </button>
              <button
                onClick={() => setActiveTab('equipment')}
                className={`px-6 py-3 font-body text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'equipment' ? 'bg-accent text-primary' : 'text-technical-gray hover:text-foreground'}`
                }>

                Equipment
              </button>
              <button
                onClick={() => setActiveTab('quality')}
                className={`px-6 py-3 font-body text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'quality' ? 'bg-accent text-primary' : 'text-technical-gray hover:text-foreground'}`
                }>

                Quality Systems
              </button>
            </div>
          </div>

          {activeTab === 'facilities' &&
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {facilities.map((facility) =>
            <div key={facility.id} className="group bg-card border border-border rounded-xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow">
                  <div className="relative h-48 overflow-hidden">
                    <AppImage
                  src={facility.image}
                  alt={facility.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                  </div>
                  <div className="p-6">
                    <h3 className="font-headline text-xl font-bold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                      {facility.name}
                    </h3>
                    <p className="font-body text-sm text-technical-gray mb-4">
                      {facility.location} • {facility.size}
                    </p>
                    <div className="mb-4">
                      <p className="font-cta text-xs text-accent mb-2">CAPABILITIES</p>
                      <ul className="space-y-1">
                        {facility.capabilities.map((cap, idx) =>
                    <li key={idx} className="flex items-start space-x-2">
                            <Icon name="CheckCircleIcon" size={16} className="text-aerospace-green mt-0.5 flex-shrink-0" />
                            <span className="font-body text-sm text-foreground">{cap}</span>
                          </li>
                    )}
                      </ul>
                    </div>
                    <div>
                      <p className="font-cta text-xs text-accent mb-2">CERTIFICATIONS</p>
                      <div className="flex flex-wrap gap-2">
                        {facility.certifications.map((cert, idx) =>
                    <span key={idx} className="px-2 py-1 bg-muted text-technical-gray font-body text-xs rounded border border-border">
                            {cert}
                          </span>
                    )}
                      </div>
                    </div>
                  </div>
                </div>
            )}
            </div>
          }

          {activeTab === 'equipment' &&
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {equipment.map((item, index) =>
            <div key={index} className="bg-card border border-border rounded-xl p-6 hover:border-accent transition-all duration-300 hover:shadow-glow">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as any} size={24} className="text-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-headline text-lg font-bold text-foreground mb-1">
                        {item.name}
                      </h3>
                      <p className="font-accent text-2xl text-accent">{item.count}</p>
                    </div>
                  </div>
                </div>
            )}
            </div>
          }

          {activeTab === 'quality' &&
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {qualityCapabilities.map((capability, index) =>
            <div key={index} className="bg-card border border-border rounded-xl p-8 hover:border-accent transition-all duration-300 hover:shadow-glow">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon name={capability.icon as any} size={24} className="text-accent" />
                  </div>
                  <h3 className="font-headline text-xl font-bold text-foreground mb-3">
                    {capability.title}
                  </h3>
                  <p className="font-body text-technical-gray leading-relaxed">
                    {capability.description}
                  </p>
                </div>
            )}
            </div>
          }
        </div>
      </div>
    </section>);

};

export default FacilityCapabilities;